// Typed search filters: `in:#channel`, `in:@person|agent`, `from:@person`, `from:me`, `has:link`,
// `mentions:me`. One grammar for every entry point (header bar, mobile overlay); a resolved filter is
// applied through the same setters the header Search button uses, so a typed filter and a chip are
// the same thing.

export type SearchToken =
  | { kind: "in"; sigil: "#" | "@"; name: string }
  // `in:Docs` / `in:Tasks`: a whole result section, not a place (no sigil, no id).
  | { kind: "section"; section: "pages" | "tasks" }
  | { kind: "from"; name: string }
  | { kind: "fromMe" }
  | { kind: "hasLink" }
  | { kind: "hasFile" }
  | { kind: "mentionsMe" }
  | { kind: "mentions"; name: string }
  // A key we know with a value we can't honour (e.g. `has:pdf`): shown, never dropped.
  | { kind: "unsupported" };

export type PositionedToken = SearchToken & { raw: string; start: number; end: number };

const KEY = /^(in|from|has|mentions):/i;
// A word, keeping a quoted value (`in:"#my channel"`) whole; an unclosed quote runs to the end.
const WORD = /\S*"[^"]*("|$)\S*|\S+/g;

const unquote = (v: string): string => v.replace(/^"(.*?)"?$/, "$1").trim();

function token(word: string): SearchToken | undefined {
  const key = KEY.exec(word)?.[1]?.toLowerCase();
  if (!key) return undefined;
  const value = unquote(word.slice(key.length + 1));
  const lower = value.toLowerCase();
  if (key === "in") {
    const sigil = value[0];
    const name = unquote(value.slice(1));
    if ((sigil === "#" || sigil === "@") && name) return { kind: "in", sigil, name };
    // A sigil-less section label: `in:Docs` → pages, `in:Tasks` → tasks (case-insensitive).
    if (lower === "docs") return { kind: "section", section: "pages" };
    if (lower === "tasks") return { kind: "section", section: "tasks" };
    return { kind: "unsupported" };
  }
  if (key === "from") {
    if (lower === "me" || lower === "@me") return { kind: "fromMe" };
    const name = unquote(value.replace(/^@/, ""));
    return name ? { kind: "from", name } : { kind: "unsupported" };
  }
  if (key === "has") {
    if (lower === "link") return { kind: "hasLink" };
    // `file` = the message carries an attachment (not a file-contents search).
    if (lower === "file") return { kind: "hasFile" };
    return { kind: "unsupported" };
  }
  if (lower === "me" || lower === "@me") return { kind: "mentionsMe" };
  const mentioned = unquote(value.replace(/^@/, ""));
  return mentioned ? { kind: "mentions", name: mentioned } : { kind: "unsupported" };
}

const quoteOpen = (word: string): boolean => (word.match(/"/g)?.length ?? 0) % 2 === 1;

/**
 * The filters in `text`. With `caret` (typing), only words already finished — followed by
 * whitespace and before the caret — count, so editing mid-text or an unclosed quote never
 * absorbs early. Without it (paste, Enter) every word counts.
 */
export function parseSearchQuery(text: string, caret?: number): PositionedToken[] {
  const out: PositionedToken[] = [];
  for (const m of text.matchAll(WORD)) {
    const start = m.index ?? 0;
    const end = start + m[0].length;
    if (caret !== undefined && !(end < caret && /\s/.test(text[end] ?? ""))) continue;
    if (quoteOpen(m[0])) continue;
    const t = token(m[0]);
    if (t) out.push({ ...t, raw: m[0], start, end });
  }
  return out;
}

/** `text` without the given tokens, everything else (order, spaces) as typed. */
export function removeTokens(text: string, tokens: readonly PositionedToken[]): string {
  let out = text;
  for (const t of [...tokens].sort((a, b) => b.start - a.start)) {
    const after = /\s/.test(out[t.end] ?? "") ? t.end + 1 : t.end;
    out = out.slice(0, t.start) + out.slice(after);
  }
  return out;
}

/** Case- and accent-insensitive name key, so `in:@José` finds "jose" and `#General` finds "general". */
export function nameKey(name: string): string {
  return name.normalize("NFD").replace(/\p{M}/gu, "").trim().toLowerCase();
}

export interface NamedCandidate {
  id: string;
  name: string;
  email?: string | null;
}

/** Full name first; else a unique first-name or email-handle match. Several = ambiguous. */
export function resolveName<T extends NamedCandidate>(
  name: string,
  candidates: readonly T[],
): { match: T } | { ambiguous: number } | { missing: true } {
  const key = nameKey(name);
  const exact = candidates.find((c) => nameKey(c.name) === key);
  if (exact) return { match: exact };
  const near = candidates.filter(
    (c) =>
      nameKey(c.name.split(/\s+/)[0] ?? "") === key ||
      (c.email ? nameKey(c.email.split("@")[0] ?? "") === key : false),
  );
  if (near.length === 1 && near[0]) return { match: near[0] };
  return near.length > 1 ? { ambiguous: near.length } : { missing: true };
}
